<template>
  <transition name="global-dialog-fade">
    <div
      v-if="dialog.visible"
      class="global-dialog-overlay"
      @click.self="handleOverlayClick"
    >
      <div
        class="global-dialog"
        :class="{
          'is-confirm': dialog.type === 'confirm',
          'is-dangerous': dialog.dangerous
        }"
        role="dialog"
        aria-modal="true"
        :aria-label="dialog.title"
        @click.stop
      >
        <h3 v-if="dialog.title" class="global-dialog-title">{{ dialog.title }}</h3>
        <p class="global-dialog-message">{{ dialog.message }}</p>
        <input
          v-if="dialog.type === 'prompt'"
          ref="inputField"
          v-model="dialog.inputValue"
          class="global-dialog-input"
          type="text"
          :placeholder="dialog.placeholder"
          :maxlength="dialog.maxLength > 0 ? dialog.maxLength : undefined"
          :aria-label="dialog.message || dialog.title"
          @keydown="handleInputKeydown"
        />
        <div class="global-dialog-actions">
          <button
            v-if="dialog.showCancel"
            type="button"
            class="global-dialog-btn secondary"
            @click="cancelDialog"
          >
            {{ dialog.cancelText }}
          </button>
          <button
            ref="primaryButton"
            type="button"
            class="global-dialog-btn primary"
            :class="{ dangerous: dialog.dangerous }"
            @click="confirmDialog"
          >
            {{ dialog.confirmText }}
          </button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { dialogState, resolveDialog } from '@/utils/dialog';

export default {
  name: 'GlobalDialog',
  computed: {
    dialog() {
      return dialogState;
    },
    isDialogVisible() {
      return dialogState.visible;
    }
  },
  watch: {
    isDialogVisible(visible) {
      if (visible) {
        this.lockBodyScroll();
        this.$nextTick(() => {
          const isPrompt = this.dialog.type === 'prompt';
          const target = isPrompt ? this.$refs.inputField : this.$refs.primaryButton;
          if (target && typeof target.focus === 'function') {
            target.focus();
            if (isPrompt && typeof target.select === 'function') {
              target.select();
            }
          }
        });
      } else {
        this.unlockBodyScroll();
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown);
    this.unlockBodyScroll();
  },
  methods: {
    confirmDialog() {
      resolveDialog(this.dialog.type === 'prompt' ? (this.dialog.inputValue == null ? '' : this.dialog.inputValue) : true);
    },
    cancelDialog() {
      resolveDialog(this.dialog.type === 'prompt' ? null : false);
    },
    handleInputKeydown(event) {
      // 输入法组合中的回车/ESC 不应触发确认或取消
      if (event.isComposing || event.keyCode === 229) {
        return;
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.confirmDialog();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.cancelDialog();
      }
    },
    handleOverlayClick() {
      if (!this.dialog.closeOnOverlay) {
        return;
      }

      if (this.dialog.showCancel) {
        this.cancelDialog();
      } else {
        this.confirmDialog();
      }
    },
    handleKeydown(event) {
      if (!this.dialog.visible) {
        return;
      }

      if (event.isComposing || event.keyCode === 229) {
        return;
      }

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        if (this.dialog.showCancel) {
          this.cancelDialog();
        } else {
          this.confirmDialog();
        }
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        this.confirmDialog();
      }
    },
    lockBodyScroll() {
      if (typeof document === 'undefined') {
        return;
      }

      if (this.previousBodyOverflow === undefined) {
        this.previousBodyOverflow = document.body.style.overflow;
      }

      document.body.style.overflow = 'hidden';
    },
    unlockBodyScroll() {
      if (typeof document === 'undefined') {
        return;
      }

      document.body.style.overflow = this.previousBodyOverflow || '';
      this.previousBodyOverflow = undefined;
    }
  }
};
</script>

<style scoped>
.global-dialog-fade-enter-active,
.global-dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.global-dialog-fade-enter,
.global-dialog-fade-leave-to {
  opacity: 0;
}

.global-dialog-overlay {
  position: fixed;
  inset: 0;
  z-index: 11000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.46);
  backdrop-filter: blur(10px);
}

.global-dialog {
  width: min(440px, 100%);
  padding: 24px;
  background: var(--bg-message-assistant, #ffffff);
  color: var(--text-primary, #111827);
  border: 1px solid rgba(148, 163, 184, 0.16);
  border-radius: 24px;
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.22);
  animation: globalDialogPop 0.2s ease-out;
}

.global-dialog-title {
  margin: 0 0 10px;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
}

.global-dialog-message {
  margin: 0;
  color: var(--text-secondary, #475569);
  font-size: 15px;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

.global-dialog-input {
  width: 100%;
  box-sizing: border-box;
  margin-top: 16px;
  padding: 10px 14px;
  color: var(--text-primary, #111827);
  background: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(148, 163, 184, 0.4);
  border-radius: 12px;
  font-size: 15px;
  line-height: 1.5;
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.global-dialog-input::placeholder {
  color: var(--text-secondary, #94a3b8);
  opacity: 0.8;
}

.global-dialog-input:focus {
  border-color: rgba(37, 99, 235, 0.6);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.18);
}

.global-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
}

.global-dialog-btn {
  min-width: 92px;
  padding: 10px 18px;
  border: none;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.18s ease, opacity 0.18s ease, box-shadow 0.18s ease;
}

.global-dialog-btn:hover {
  transform: translateY(-1px);
}

.global-dialog-btn:focus-visible {
  outline: 2px solid rgba(59, 130, 246, 0.35);
  outline-offset: 2px;
}

.global-dialog-btn.secondary {
  color: var(--text-primary, #111827);
  background: rgba(148, 163, 184, 0.14);
}

.global-dialog-btn.primary {
  color: #ffffff;
  background: linear-gradient(135deg, #2563eb, #38bdf8);
  box-shadow: 0 14px 30px rgba(37, 99, 235, 0.26);
}

.global-dialog-btn.primary.dangerous {
  background: linear-gradient(135deg, #dc2626, #f97316);
  box-shadow: 0 14px 30px rgba(220, 38, 38, 0.24);
}

[data-theme="dark"] .global-dialog-overlay {
  background: rgba(2, 6, 23, 0.7);
}

[data-theme="dark"] .global-dialog {
  border-color: rgba(148, 163, 184, 0.12);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.5);
}

[data-theme="dark"] .global-dialog-btn.secondary {
  background: rgba(148, 163, 184, 0.18);
}

[data-theme="dark"] .global-dialog-input {
  background: rgba(15, 23, 42, 0.4);
  border-color: rgba(148, 163, 184, 0.3);
}

@keyframes globalDialogPop {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}
</style>
